import { tzOpts } from "@/lib/tz";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FileCheck2, Wifi, Search, Save, Trash2 } from "lucide-react";

const inputCls = "bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500";

const RESULT_BADGE = {
  BAIXAR: { label: "Pronto para baixar", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  REVISAR_VALOR: { label: "Revisar valor", cls: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  OK: { label: "Já baixado na EFI", cls: "bg-slate-800 text-slate-400 border-slate-700" },
  OUTRO_STATUS: { label: "Outro status", cls: "bg-slate-800 text-slate-400 border-slate-700" },
  NAO_ENCONTRADO: { label: "Não encontrado", cls: "bg-red-500/10 text-red-400 border-red-500/30" },
  ERRO: { label: "Erro", cls: "bg-red-500/10 text-red-400 border-red-500/30" },
};

function fmtMoney(v) {
  return parseFloat(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDate(s) {
  if (!s) return "—";
  // Datas "AAAA-MM-DD" (com ou sem hora) vem do servidor ja no horario local: formata direto
  // da string. new Date("2026-09-21") seria meia-noite UTC e apareceria um dia antes no Brasil.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}

export default function EfiBoletoSettings() {
  const [cfg, setCfg] = useState(null); // o que esta salvo no servidor
  const [form, setForm] = useState({ client_id: "", client_secret: "", environment: "production", settle_enabled: false, auto_settle: false });
  const [saving, setSaving] = useState(false);
  const [testId, setTestId] = useState("");
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);
  const [days, setDays] = useState("30");
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null); // { items, counts, total_in_period, next_offset }
  const [selected, setSelected] = useState({});
  const [settling, setSettling] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/tenant/efi-boleto");
      setCfg(data);
      setForm({ client_id: data.client_id || "", client_secret: "", environment: data.environment || "production", settle_enabled: !!data.settle_enabled, auto_settle: !!data.auto_settle });
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/tenant/efi-boleto", form);
      toast.success("Credenciais de boletos salvas.");
      if (form.settle_enabled && !data.settle_enabled) {
        toast.message("A baixa continua desligada: ao trocar o Client ID, marque 'Permitir baixar boletos' de novo e salve.");
      }
      await load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remover as credenciais de boletos da EFI deste painel?")) return;
    try {
      await api.delete("/tenant/efi-boleto");
      setScan(null); setTestRes(null);
      toast.success("Credenciais removidas.");
      await load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const test = async () => {
    setTesting(true); setTestRes(null);
    try {
      const { data } = await api.post("/tenant/efi-boleto/test", { charge_id: testId.trim() || null });
      setTestRes(data);
    } catch (ex) {
      setTestRes({ ok: false, error: formatApiError(ex) });
    } finally {
      setTesting(false);
    }
  };

  const runScan = async (offset = 0) => {
    setScanning(true);
    try {
      const { data } = await api.post("/tenant/efi-boleto/scan", { days: parseInt(days, 10), offset });
      if (offset === 0) {
        setScan(data); setSelected({});
      } else {
        setScan((prev) => ({
          ...data,
          items: [...(prev?.items || []), ...data.items],
          counts: Object.fromEntries(
            [...new Set([...Object.keys(prev?.counts || {}), ...Object.keys(data.counts || {})])]
              .map((k) => [k, (prev?.counts?.[k] || 0) + (data.counts?.[k] || 0)])
          ),
        }));
      }
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setScanning(false);
    }
  };

  const runScanAll = async () => {
    setScanning(true);
    try {
      let items = [];
      const counts = {};
      let off = 0;
      for (let n = 0; n < 60; n++) {
        const { data } = await api.post("/tenant/efi-boleto/scan", { days: parseInt(days, 10), offset: off });
        items = items.concat(data.items);
        for (const [k, v] of Object.entries(data.counts || {})) counts[k] = (counts[k] || 0) + v;
        setScan({ ...data, items: [...items], counts: { ...counts } });
        if (data.next_offset == null) break;
        off = data.next_offset;
      }
      setSelected({});
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setScanning(false);
    }
  };

  const MAX_SETTLE = 20; // limite do servidor por chamada
  const toBaixar = (scan?.items || []).filter((i) => i.result === "BAIXAR");
  const q = search.trim().toLowerCase();
  const visibleItems = (scan?.items || [])
    .filter((i) => !q || [i.title_id, i.client_name, i.login, i.charge_id].some((v) => String(v ?? "").toLowerCase().includes(q)))
    .sort((a, b) => (b.result === "BAIXAR") - (a.result === "BAIXAR"));
  const visibleReady = visibleItems.filter((i) => i.result === "BAIXAR");
  const chosen = toBaixar.filter((i) => selected[i.title_id]);

  const settle = async () => {
    if (chosen.length === 0) return;
    const ok = window.confirm(
      `Baixar ${chosen.length} boleto(s) na EFI?\n\n` +
      "Isso marca o boleto como pago manualmente (o mesmo que você faz no painel da EFI). " +
      "Só será feito para títulos que continuam PAGOS no mk-auth e com o boleto ainda aguardando na EFI — tudo é conferido de novo na hora."
    );
    if (!ok) return;
    setSettling(true);
    try {
      const { data } = await api.post("/tenant/efi-boleto/settle", { title_ids: chosen.map((i) => i.title_id), confirm: true });
      const byId = Object.fromEntries(data.results.map((r) => [r.title_id, r]));
      setScan((prev) => ({
        ...prev,
        items: prev.items.map((i) => {
          const r = byId[i.title_id];
          if (!r) return i;
          if (r.result === "BAIXADO") return { ...i, result: "OK", detail: "baixado agora" };
          return { ...i, detail: `${r.result}: ${r.reason}` };
        }),
      }));
      setSelected({});
      const bad = data.results.filter((r) => r.result !== "BAIXADO");
      if (data.baixados > 0) toast.success(`${data.baixados} boleto(s) baixado(s) na EFI.`);
      if (bad.length > 0) toast.warning(`${bad.length} não foram baixados — veja o motivo na lista.`);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSettling(false);
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4" data-testid="efi-boleto-card">
      <div className="flex items-center gap-2">
        <FileCheck2 className="w-4 h-4 text-emerald-400" />
        <div className="text-sm text-slate-300 font-medium">EFI · Baixa de boletos</div>
      </div>
      <p className="text-xs text-slate-500">
        Opcional. Para empresas cujos boletos são emitidos pelo mk-auth via EFI: quando o cliente paga via PIX, o boleto
        continua em aberto na EFI. Informe aqui as credenciais da <b>aplicação da EFI que emite os boletos</b> (a do
        mk-auth, com a permissão “Emissão de cobranças”) para o CobraZap conferir e baixar esses boletos. É independente
        das credenciais de PIX acima e nunca baixa um boleto cujo título não esteja pago no mk-auth.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1 block">Client ID (boletos)</label>
          <Input data-testid="efi-boleto-client-id" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1 block">Client Secret (boletos)</label>
          <Input
            data-testid="efi-boleto-client-secret" type="password" value={form.client_secret}
            onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
            placeholder={cfg?.has_secret ? "•••••••• (salvo — deixe em branco para manter)" : ""}
            className={inputCls} autoComplete="new-password"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1 block">Ambiente</label>
          <select
            data-testid="efi-boleto-env" value={form.environment}
            onChange={(e) => setForm({ ...form, environment: e.target.value })}
            className={`${inputCls} h-9 text-sm rounded-md border px-2 w-full`}
          >
            <option value="production">Produção</option>
            <option value="sandbox">Homologação</option>
          </select>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-md border border-slate-800 bg-slate-950/50 p-3">
        <Switch
          data-testid="efi-boleto-settle-switch"
          checked={form.settle_enabled}
          onCheckedChange={(v) => setForm({ ...form, settle_enabled: v, auto_settle: v ? form.auto_settle : false })}
        />
        <div className="text-xs text-slate-400">
          <div className="text-slate-300 font-medium">Permitir baixar boletos</div>
          Desligado por padrão. Com ele desligado, só é possível testar e verificar (somente leitura). Ligado, aparece o
          botão “Baixar selecionados” na verificação — sempre com confirmação, e só para boletos prontos.
        </div>
      </div>

      <div className={`flex items-start gap-3 rounded-md border border-slate-800 bg-slate-950/50 p-3 ${form.settle_enabled ? "" : "opacity-50"}`}>
        <Switch
          data-testid="efi-boleto-auto-switch"
          checked={form.auto_settle && form.settle_enabled}
          disabled={!form.settle_enabled}
          onCheckedChange={(v) => setForm({ ...form, auto_settle: v })}
        />
        <div className="text-xs text-slate-400">
          <div className="text-slate-300 font-medium">Baixar automaticamente (a cada 15 minutos)</div>
          Baixa sozinho, na EFI, o boleto dos títulos pagos <b>via PIX</b> nos últimos 5 dias, com as mesmas conferências da
          baixa manual (pago no mk-auth, mesmo valor, boleto ainda em aberto na EFI) e no máximo 30 por rodada. Grava no
          histórico do boleto o motivo, o TXID e o E2E do PIX. Exige “Permitir baixar boletos” ligado. Lembre de salvar.
          {cfg?.auto_settle && (
            <div className="mt-1 text-slate-500" data-testid="efi-boleto-auto-last">
              {cfg.auto_last_run
                ? <>Última execução: {new Date(cfg.auto_last_run.at).toLocaleString("pt-BR", tzOpts())} —{" "}
                    {cfg.auto_last_run.error
                      ? `erro: ${cfg.auto_last_run.error}`
                      : `${cfg.auto_last_run.checked} verificados, ${cfg.auto_last_run.baixados} baixados${cfg.auto_last_run.atencao ? `, ${cfg.auto_last_run.atencao} pedem atenção (veja a auditoria)` : ""}`}</>
                : "Ainda não executou: a primeira rodada acontece em até 15 minutos."}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button data-testid="efi-boleto-save" onClick={save} disabled={saving || !form.client_id.trim()} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Salvando…" : "Salvar credenciais de boletos"}
        </Button>
        {cfg?.client_id && (
          <Button data-testid="efi-boleto-remove" variant="outline" onClick={remove} className="border-slate-700 hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-300">
            <Trash2 className="w-4 h-4 mr-2" /> Remover
          </Button>
        )}
      </div>

      {cfg?.client_id && cfg?.has_secret && (
        <>
          <div className="border-t border-slate-800 pt-4 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Testar conexão</div>
            <div className="flex gap-2 flex-wrap items-center">
              <Input
                data-testid="efi-boleto-test-id" value={testId} onChange={(e) => setTestId(e.target.value)}
                placeholder="ID de um boleto (opcional) — ex.: o número da cobrança na EFI"
                className={`${inputCls} h-9 text-sm w-96 max-w-full`}
              />
              <Button data-testid="efi-boleto-test" size="sm" variant="outline" onClick={test} disabled={testing} className="border-slate-700">
                <Wifi className="w-4 h-4 mr-2" /> {testing ? "Testando…" : "Testar conexão"}
              </Button>
            </div>
            {testRes && (
              <div data-testid="efi-boleto-test-result" className={`text-xs ${testRes.ok ? "text-emerald-300" : "text-red-300"}`}>
                {testRes.authorized === false && <>Credenciais recusadas: {testRes.error}</>}
                {testRes.authorized && !testRes.charge && !testRes.error && <>✔ Credenciais aceitas pela EFI.</>}
                {testRes.charge && (
                  <>✔ Credenciais aceitas. Boleto {testRes.charge.charge_id}: status <b>{testRes.charge.status}</b>
                    {testRes.charge.total_cents != null && <> · {fmtMoney(testRes.charge.total_cents / 100)}</>}
                    {testRes.charge.expire_at && <> · vence {fmtDate(testRes.charge.expire_at)}</>}</>
                )}
                {testRes.authorized && testRes.charge_found === false && <>Credenciais aceitas, mas o boleto não foi encontrado: {testRes.error}</>}
              </div>
            )}
          </div>

          <div className="border-t border-slate-800 pt-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Verificar boletos pagos (somente leitura)</div>
            <p className="text-xs text-slate-500">
              Lista os títulos pagos no mk-auth no período e mostra como está o boleto de cada um na EFI. Não baixa nada.
            </p>
            <div className="flex gap-2 items-center flex-wrap">
              <select
                data-testid="efi-boleto-days" value={days} onChange={(e) => setDays(e.target.value)}
                className={`${inputCls} h-9 text-sm rounded-md border px-2`}
              >
                <option value="7">Últimos 7 dias</option>
                <option value="15">Últimos 15 dias</option>
                <option value="30">Últimos 30 dias</option>
                <option value="60">Últimos 60 dias</option>
                <option value="120">Últimos 120 dias</option>
              </select>
              <Button data-testid="efi-boleto-scan" size="sm" onClick={() => runScan(0)} disabled={scanning} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold h-9">
                <Search className="w-4 h-4 mr-2" /> {scanning ? "Verificando…" : "Verificar boletos pagos"}
              </Button>
            </div>

            {scan && (
              <div className="space-y-2" data-testid="efi-boleto-scan-result">
                <div className="flex flex-wrap gap-2 text-xs text-slate-400 items-center">
                  <span>{scan.items.length} de {scan.total_in_period} verificados:</span>
                  {Object.entries(scan.counts || {}).map(([k, n]) => (
                    <Badge key={k} className={`${(RESULT_BADGE[k] || RESULT_BADGE.OUTRO_STATUS).cls} text-[10px]`}>
                      {(RESULT_BADGE[k] || { label: k }).label}: {n}
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  {scan.next_offset != null && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => runScan(scan.next_offset)} disabled={scanning} className="border-slate-700 text-xs">
                        Verificar mais
                      </Button>
                      <Button data-testid="efi-boleto-scan-all" size="sm" variant="outline" onClick={runScanAll} disabled={scanning} className="border-slate-700 text-xs">
                        {scanning ? "Verificando…" : `Verificar tudo (${scan.total_in_period})`}
                      </Button>
                    </>
                  )}
                  <input
                    data-testid="efi-boleto-search" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por título, cliente ou boleto"
                    className={`${inputCls} h-8 text-xs rounded-md border px-2 w-64`}
                  />
                  {q && <span className="text-xs text-slate-500">{visibleItems.length} resultado(s)</span>}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                        <th className="py-2 pr-2 w-6"></th>
                        <th className="py-2 pr-3">Cliente</th>
                        <th className="py-2 pr-3">Valor</th>
                        <th className="py-2 pr-3">Pago em</th>
                        <th className="py-2 pr-3">Boleto (EFI)</th>
                        <th className="py-2 pr-3">Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleItems.map((i) => {
                        const b = RESULT_BADGE[i.result] || RESULT_BADGE.OUTRO_STATUS;
                        return (
                          <tr key={i.title_id} className="border-b border-slate-800/60 align-top">
                            <td className="py-2 pr-2">
                              {i.result === "BAIXAR" && cfg?.settle_enabled && (
                                <input
                                  type="checkbox" checked={!!selected[i.title_id]}
                                  onChange={(e) => setSelected({ ...selected, [i.title_id]: e.target.checked })}
                                  data-testid={`efi-boleto-select-${i.title_id}`}
                                />
                              )}
                            </td>
                            <td className="py-2 pr-3">
                              <div className="text-white">{i.client_name || i.login}</div>
                              <div className="font-mono-jb text-slate-500">título {i.title_id}</div>
                            </td>
                            <td className="py-2 pr-3 font-mono-jb text-slate-300">{fmtMoney(i.valor)}</td>
                            <td className="py-2 pr-3 text-slate-400">{fmtDate(i.datapag)}</td>
                            <td className="py-2 pr-3 font-mono-jb text-slate-400">
                              {i.charge_id}
                              <div className="text-slate-500">{i.efi_status || "—"}</div>
                            </td>
                            <td className="py-2 pr-3">
                              <Badge className={`${b.cls} text-[10px]`}>{b.label}</Badge>
                              <div className="text-[10px] text-slate-500 mt-1">{i.detail}</div>
                            </td>
                          </tr>
                        );
                      })}
                      {visibleItems.length === 0 && (
                        <tr><td colSpan={6} className="py-4 text-center text-slate-500">Nenhum título pago com boleto EFI neste período.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  {scan.next_offset != null && (
                    <Button size="sm" variant="outline" onClick={() => runScan(scan.next_offset)} disabled={scanning} className="border-slate-700 text-xs">
                      Verificar mais
                    </Button>
                  )}
                  {cfg?.settle_enabled && toBaixar.length > 0 && (
                    <>
                      <Button size="sm" variant="outline" className="border-slate-700 text-xs"
                        onClick={() => setSelected(Object.fromEntries(visibleReady.slice(0, MAX_SETTLE).map((i) => [i.title_id, true])))}>
                        Selecionar {Math.min(visibleReady.length, MAX_SETTLE)} prontos (máx. {MAX_SETTLE} por vez)
                      </Button>
                      <Button data-testid="efi-boleto-settle" size="sm" onClick={settle} disabled={settling || chosen.length === 0}
                        className="bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold text-xs">
                        {settling ? "Baixando…" : `Baixar selecionados (${chosen.length}) na EFI`}
                      </Button>
                    </>
                  )}
                  {!cfg?.settle_enabled && toBaixar.length > 0 && (
                    <span className="text-xs text-slate-500">Para baixar, ligue “Permitir baixar boletos” acima e salve.</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
